import type { ApiResponse, MachineDetailDto, RouterMachinesResponse } from "../../../shared/types/api";
import type {
  MachineEntity,
  RegisterInstalledMachineRequest,
} from "../../../domain/types/machine";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";

class AdminMachineApiService {
  async getMachines(companyId: number): Promise<ApiResponse<MachineDetailDto[]>> {
    const response = await httpClient.get<ApiResponse<MachineDetailDto[]>>(
      NEW_API_ENDPOINTS.adminMachineController.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "장비 정보 불러오기 실패.");
    }

    return response.data;
  }

  async getMachineDetailsByRouter(companyId: number): Promise<ApiResponse<RouterMachinesResponse[]>> {
    const response = await httpClient.get<ApiResponse<RouterMachinesResponse[]>>(
      NEW_API_ENDPOINTS.adminMachineDetailsByRouter.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "장비 정보 불러오기 실패.");
    }

    return response.data;
  }

  /**
   * 탐색되지 않는 장비를 등록한다.
   *
   * IP·MAC 은 보내지 않는다 — 장비에 없는 값이라 서버가 채운다.
   */
  async registerInstalledMachine(
    request: RegisterInstalledMachineRequest,
  ): Promise<ApiResponse<MachineEntity>> {
    const response = await httpClient.post<ApiResponse<MachineEntity>>(
      NEW_API_ENDPOINTS.adminInstalledMachineController.REGISTER,
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "장비 등록에 실패했습니다.");
    }

    return response.data;
  }

  /** 라우터에 등록된 장비 목록 */
  async getMachinesByRouter(routerId: number): Promise<ApiResponse<MachineEntity[]>> {
    const response = await httpClient.get<ApiResponse<MachineEntity[]>>(
      NEW_API_ENDPOINTS.adminInstalledMachineController.GET_BY_ROUTER(routerId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "장비 목록을 불러오지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 별칭만 수정한다. 빈 문자열을 보내면 별칭이 지워지고 장비제품 이름으로 되돌아간다.
   */
  async updateMachineAlias(
    machineId: number,
    alias: string | null,
  ): Promise<ApiResponse<MachineEntity>> {
    const response = await httpClient.patch<ApiResponse<MachineEntity>>(
      NEW_API_ENDPOINTS.adminInstalledMachineController.UPDATE_ALIAS(machineId),
      { alias },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "별칭을 수정하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * MES 화면 노출 여부를 바꾼다. 수집과 KPI 는 영향을 받지 않는다.
   */
  async updateMachineVisibility(
    machineId: number,
    hiddenInMes: boolean,
  ): Promise<ApiResponse<MachineEntity>> {
    const response = await httpClient.patch<ApiResponse<MachineEntity>>(
      NEW_API_ENDPOINTS.adminInstalledMachineController.UPDATE_VISIBILITY(machineId),
      { hiddenInMes },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "노출 설정을 바꾸지 못했습니다.");
    }

    return response.data;
  }

  /** 장비 삭제 */
  async deleteMachine(machineId: number): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.adminInstalledMachineController.DELETE(machineId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "장비 삭제에 실패했습니다.");
    }

    return response.data;
  }
}

export const adminMachineApiService = new AdminMachineApiService();